import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Leaf, Mail } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      loginWithToken(res.data.token, res.data.user);
      toast.success("Welcome back!");
      const u = res.data.user;
      navigate(u.role === "admin" ? "/admin" : u.role === "provider" ? "/provider" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem("ll_pending_role", "customer");
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block hero-gradient grain relative">
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-lawn flex items-center justify-center"><Leaf className="w-5 h-5" /></div>
            <span className="font-display text-xl font-extrabold">LawnLink<span className="text-lawn">Pro</span></span>
          </Link>
          <div>
            <h2 className="font-display text-4xl font-extrabold leading-tight">Welcome back to the greenest marketplace.</h2>
            <p className="text-white/60 mt-4 max-w-sm">Manage bookings, track earnings, and grow your business — all in one place.</p>
          </div>
          <p className="text-white/40 text-sm">© 2026 LawnLink Pro</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-lawn flex items-center justify-center"><Leaf className="w-5 h-5 text-white" /></div>
            <span className="font-display text-xl font-extrabold text-lawn-ink">LawnLink<span className="text-lawn">Pro</span></span>
          </Link>
          <h1 className="font-display text-3xl font-extrabold text-lawn-ink">Log in</h1>
          <p className="text-muted-foreground mt-1">Access your dashboard</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-xl" placeholder="you@email.com" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 rounded-xl" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-xl bg-lawn hover:bg-lawn-dark font-semibold h-11">{loading ? "Logging in…" : "Log in"}</Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="flex-1 h-px bg-border" />
          </div>
          <Button data-testid="login-google" onClick={googleLogin} variant="outline" className="w-full rounded-xl h-11 gap-2 font-semibold">
            <Mail className="w-4 h-4" /> Continue with Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            New here? <Link to="/signup" className="text-lawn font-semibold hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
